import {
  IMPORT_FSP_PROFILE,
  LOAD_FSP_PROFILE,
  UPDATE_FSP_PROFILE,
} from 'model/export';
import { selector as fspImportDataSelector } from 'reducers/fspImport';
import { put, select, takeEvery, call } from 'redux-saga/effects';
import { createFspConfig } from 'sagas/fspConfig';
import { importLimits } from 'sagas/limit';
import { importPayerTypes } from 'sagas/payerType';
import { importPaynets } from 'sagas/fspPaynet';
import { importRtn } from 'sagas/rtn';
import { importSSOURIs } from 'sagas/ssoUri';
import { importUserRoles } from 'sagas/userRole';
import { loadFSPProfile } from 'sdk/export';
import {
  load as loadLoanAuthOptions,
  create as createLoanAuthOption,
} from 'sdk/fspLoanAuthOption';
import { LOAD } from 'model/fspLoanAuthOption';
import { create as createIfpEnrollment } from 'model/palIfpEnrolled';
import { UPDATE as UPDATE_FSP_CONFIG_VALUES } from 'model/fspConfigValues';
import { sanitizeForImport } from 'model/util/fspConfig';
import { updateFSPCoreConfig } from 'sagas/fspCoreConfig';
import { updateFsp } from 'sagas/fsp';
import { updateOneTimePasscodeEventConfig } from 'sagas/oneTimePasscodeEventConfig';
import { ON } from 'consts';
import { createActionSuccess } from '../util';
import { getAuth } from '../util/auth';
import { createFspPalConfig } from './fspConfigPayALoan';
import { createFspProcessingFees } from './fspProcessingFees';
import { PaymentPurposeTemplate } from '../components/modules/implementation/Fsp/Detail/PayALoan/GeneralConfigs/consts';
import applyNestedDefaults from '../util/applyNestedDefaults';
import buildExtDdaRtConfig from '../components/modules/implementation/Fsp/Detail/PayALoan/Util/buildExtDdaRtConfig';

// Custom saga to load FSP profile with loan auth options
export function* loadFSPProfileAction(action) {
  const { fspId } = action.data || {};

  try {
    const profileResponse = yield call(loadFSPProfile, { fspId });

    let loanAuthOptions = [];
    try {
      const loanAuthResponse = yield call(loadLoanAuthOptions, fspId);

      // The SDK returns the coreAuthOptions array directly, not wrapped in an object
      if (loanAuthResponse && Array.isArray(loanAuthResponse)) {
        loanAuthOptions = loanAuthResponse
          .filter((option) => option.fspLoanAuthOption) // Only enabled options
          .map((option) => ({
            coreAuthOptionId: option.id,
            authDataLabel: option.fspLoanAuthOption?.label || option.key,
            description:
              option.fspLoanAuthOption?.description || option.description,
          }));
      }
    } catch {
      // Silently continue if loan auth options cannot be loaded
    }

    const combinedResponse = {
      ...profileResponse,
      fsploanauthopt: loanAuthOptions,
    };

    yield put({
      type: LOAD_FSP_PROFILE.SUCCESS,
      data: {
        response: combinedResponse,
        props: { fspId },
      },
    });
  } catch (error) {
    yield put({
      type: LOAD_FSP_PROFILE.FAILURE,
      data: {
        error,
        props: { fspId },
      },
    });
  }
}

function* updateFSPProfile({ data }) {
  yield put(createActionSuccess(UPDATE_FSP_PROFILE)(data));
}

export function* importFSPProfile({ data: fspId }) {
  const {
    fsp,
    fspconfig,
    fspcoreconfig,
    fsppalconfig,
    fspotpevent,
    fsppayertype,
    fsppaynets,
    fspprocessingfees,
    processingfee,
    fsprtn,
    limit,
    limitrule,
    ssouri,
    userrole,
    fsploanauthopt,
    fspConfigKeyValue,
  } = yield select(fspImportDataSelector);

  const sanitizedData = { data: sanitizeForImport(fspconfig) };
  const fspConfig = yield createFspConfig()(sanitizedData);

  if (fspConfig) {
    const id = fspConfig.response.id;

    yield updateFsp()({ data: fsp });

    if (fsprtn?.length) {
      yield importRtn()({ data: { fspId, fsprtn } });
    }
    if (userrole?.length) {
      yield importUserRoles()({ data: { fspId, userrole } });
    }
    if (fsploanauthopt?.length) {
      // Get auth from store
      const auth = yield select(getAuth);

      // Create each loan auth option individually
      for (let i = 0; i < fsploanauthopt.length; i++) {
        const option = fsploanauthopt[i];
        try {
          yield call(
            createLoanAuthOption,
            {
              fspId,
              coreAuthOptionId: option.coreAuthOptionId,
              authDataLabel: option.authDataLabel,
              description: option.description,
            },
            auth
          );
        } catch {
          // Silently continue if individual option creation fails
        }
      }

      try {
        const freshOptions = yield call(loadLoanAuthOptions, fspId);
        yield put({
          type: LOAD.SUCCESS,
          data: {
            response: freshOptions,
            props: { fspId },
          },
        });
      } catch {
        // Silently continue if reload fails
      }
    }
    if (limit?.length) {
      yield importLimits()({ data: { fspId, limit, limitrule } });
    }
    if (fsppayertype?.length) {
      yield importPayerTypes()({ data: { fspId, fsppayertype } });
    }
    if (ssouri?.length) {
      yield importSSOURIs()({ data: { fspId, ssouri } });
    }
    if (fsppaynets?.length) {
      yield importPaynets()({ data: { fspId, fsppaynets } });
    }
    if (fspprocessingfees?.length) {
      for (let i = 0; i < fspprocessingfees.length; i++) {
        const z = fspprocessingfees[i];
        yield createFspProcessingFees()({
          data: { fspId, fspprocessingfees: z },
        });
      }
    }
    yield updateOneTimePasscodeEventConfig()({
      data: { fspId, ...fspotpevent },
    });
    yield updateFSPCoreConfig()({
      data: { ...fspcoreconfig, fspId, id },
    });
    if (fspConfigKeyValue?.length) {
      const configItems = fspConfigKeyValue.map((item) => {
        let value;

        if (item.dataType === 'bit') {
          value =
            item.configValue === '1' ||
            item.configValue === 1 ||
            item.configValue === true ||
            item.configValue === 'true';
        } else {
          value = item.configValue;
        }

        return {
          key: item.configKey,
          value,
        };
      });

      yield put({
        type: UPDATE_FSP_CONFIG_VALUES.ACTION,
        data: { fspId, configItems },
      });
    }
    const palConfigCreated = yield createFspPalConfig()({
      data: applyNestedDefaults(
        {
          ...fsppalconfig,
          fspId,
          extDdaRtConfig: buildExtDdaRtConfig(
            fsppalconfig.extDdaRtConfig?.featureEnabled,
            fsppalconfig.extDdaRtConfig?.delayDays
          ),
        },
        {
          paymentPurposeConfig: PaymentPurposeTemplate,
        }
      ),
    });

    if (!palConfigCreated) {
      yield put({ type: IMPORT_FSP_PROFILE.FAILURE });
      return;
    }

    // Trigger IFP enrollment provisioning if either enrolled or guest payer
    // is enabled during json import (fresh import has no prior enrollment, so
    // a single provision covers both being enabled together).
    if (
      fsppalconfig?.enrolledUserOffOn === ON ||
      fsppalconfig?.guestUserOffOn === ON
    ) {
      yield put(createIfpEnrollment(fspId));
    }
    yield put({ type: IMPORT_FSP_PROFILE.SUCCESS });
  } else {
    yield put({ type: IMPORT_FSP_PROFILE.FAILURE });
  }
}

export default function* saga() {
  yield takeEvery(LOAD_FSP_PROFILE.ACTION, loadFSPProfileAction);

  yield takeEvery(UPDATE_FSP_PROFILE.ACTION, updateFSPProfile);

  yield takeEvery(IMPORT_FSP_PROFILE.ACTION, importFSPProfile);
}
